import {
  createContext,
  useMemo,
  PropsWithChildren,
  useState,
  ReactNode,
  Dispatch,
  DispatchWithoutAction,
} from 'react'

import { createDialogAnalytics } from '@/hooks/common/Analytics/useDialogAnalytics'

export interface DialogProps {
  key: string
  close: DispatchWithoutAction
  inner?: ReactNode
  title?: string
  titleAlign?: 'left' | 'center'
  maxWidth?: 'xl' | 'lg' | 'md'
  displayClose?: boolean // 右上の閉じるボタンを表示するか
  handlePreClose?: () => void // 閉じるボタンやエスケープを押した時の挙動をカスタマイズする
}

interface Context {
  dialogs: DialogProps[]
  open: Dispatch<
    {
      render?: ({ close }: { close: DispatchWithoutAction }) => ReactNode
    } & Pick<
      DialogProps,
      'title' | 'titleAlign' | 'maxWidth' | 'displayClose' | 'handlePreClose'
    >
  >
}

const defaultContext: Context = {
  dialogs: [],
  open: () => {},
}

export const DialogContext = createContext(defaultContext)

export const DialogContextProvider = ({ children }: PropsWithChildren) => {
  const [dialogs, setDialogs] = useState<DialogProps[]>([])

  const open: Context['open'] = (options) => {
    const key = String(new Date().getTime())
    const { trackOpen, trackClose } = createDialogAnalytics(options.title, key)

    trackOpen() // ダイアログを開くイベントを追跡

    const close = () => {
      trackClose() // ダイアログを閉じるイベントを追跡

      const filtered = dialogs.filter((dialog) => dialog.key !== key)
      setDialogs(filtered)
    }

    const dialog: DialogProps = {
      ...options,
      key,
      close,
      inner: options.render ? options.render({ close }) : undefined,
    }
    setDialogs([...dialogs, dialog])
  }

  const context = useMemo(
    () => ({
      dialogs,
      open,
    }),
    [dialogs],
  )

  return (
    <DialogContext.Provider value={context}>{children}</DialogContext.Provider>
  )
}
